import { X } from "lucide-react";
import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

import useExitTransition from './useExitTransition';
import './modal.css';

/**
 * SidePanel - full-height right panel that leaves the page behind it usable.
 *
 * Unlike `Modal placement="right"`, it does not lock page scroll, trap focus,
 * or block clicks outside it, so the list it details stays interactive.
 * Escape and outside clicks close it; clicks inside elements matching
 * `keepOpenSelector` (e.g. other list items that swap the panel's content)
 * and clicks inside other dialogs, menus, or toasts do not.
 *
 * @param {Object} props
 * @param {boolean} props.show - Whether the panel is visible
 * @param {Function} props.onClose - Called on Escape, outside click, or the header close button
 * @param {string} props.className - Extra classes for the panel root (e.g. `right-drawer-applicant`)
 * @param {string} props.keepOpenSelector - Outside-click targets matching this selector keep the panel open
 * @param {React.ReactNode} props.children - `SidePanel.Header` and `SidePanel.Body`
 */
const SidePanelContext = createContext({ onClose: undefined, headerId: undefined });

const IGNORE_OUTSIDE_CLICK_SELECTOR = [
  '[data-testid="modal-overlay"]',
  '[role="dialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  '[data-sonner-toaster]',
].join(', ');

const isTypingTarget = (el) =>
  el instanceof Element &&
  (el.closest('input, textarea, select, [contenteditable="true"]') !== null);

const SidePanel = ({
  show = false,
  onClose,
  className = '',
  keepOpenSelector = '[data-side-panel-keep-open]',
  children,
}) => {
  const { isMounted, isClosing, content } = useExitTransition(show, children);
  const panelRef = useRef(null);
  const headerId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!show) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[data-testid="modal-overlay"]')) return;
      onCloseRef.current?.();
    };

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (panelRef.current?.contains(target)) return;
      if (keepOpenSelector && target.closest(keepOpenSelector)) return;
      if (target.closest(IGNORE_OUTSIDE_CLICK_SELECTOR)) return;
      onCloseRef.current?.();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [show, keepOpenSelector]);

  useEffect(() => {
    if (show && !isTypingTarget(document.activeElement)) {
      panelRef.current?.focus({ preventScroll: true });
    }
  }, [show]);

  if (!isMounted) return null;

  return createPortal(
    <div
      className={twMerge(
        'right-drawer-modal side-panel fixed inset-0 z-50 flex justify-end',
        isClosing && 'right-drawer-closing',
        className
      )}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby={headerId}
        tabIndex={-1}
        className="relative h-full max-h-full outline-none"
      >
        <div className="relative flex h-full max-h-full flex-col rounded-none border-l border-gray-200 bg-white shadow-xl dark:border-gray-600 dark:bg-gray-800 sm:rounded-l-lg">
          <SidePanelContext.Provider value={{ onClose, headerId }}>
            {content}
          </SidePanelContext.Provider>
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children - Title
 * @param {React.ReactNode} props.actions - Optional controls rendered before the close button
 */
const SidePanelHeader = ({ children, actions, className = '' }) => {
  const { onClose, headerId } = useContext(SidePanelContext);
  return (
    <div
      className={twMerge(
        'flex items-start justify-between gap-3 rounded-t border-b p-5 dark:border-gray-600',
        className
      )}
    >
      <h3
        id={headerId}
        className="min-w-0 flex-1 truncate text-xl font-medium text-gray-900 dark:text-white"
      >
        {children}
      </h3>
      <div className="ml-auto flex shrink-0 items-center gap-1">
        {actions}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
        >
          <X aria-hidden className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const SidePanelBody = forwardRef(({ children, className = '' }, ref) => (
  <div ref={ref} className={twMerge('flex-1 overflow-auto p-6', className)}>
    {children}
  </div>
));
SidePanelBody.displayName = 'SidePanel.Body';

SidePanel.Header = SidePanelHeader;
SidePanel.Body = SidePanelBody;

export default SidePanel;
